import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FavoriteService } from 'src/app/core/api-services/export/shared/responses/FavoriteServicesResponse';
import { FavoriteServicesService } from 'src/app/core/api-services/favorite-services/favorite-services.service';
import { GetAllServicesDetailService } from 'src/app/core/api-services/portal/get-all-services-detail.service';
import { ServiceMetadataGetCustomDetailService } from 'src/app/core/api-services/portal/service-metadata-get-custom-details.service';
import { PortalServiceDetailsItemDto } from 'src/app/core/models/portal/GetAllServicesDetailResponse';
import { ResponseData } from 'src/app/core/models/portal/ServiceDetailByDigitalNumberResponse';
import { ServiceMetaData } from 'src/app/core/models/shared/ServiceMetaData';
import { PortalServiceDetailsMapperService } from '../../portal/portal-service-details-mapper.service';

@Injectable()
export class ServicesListDataService {
  constructor(
    private favoriteServicesService: FavoriteServicesService,
    private getAllServicesDetailService: GetAllServicesDetailService,
    protected metadataService: ServiceMetadataGetCustomDetailService,
    private serviceDetailsMapper: PortalServiceDetailsMapperService,
  ) {}

  private _allServices: FavoriteServiceHybrid[] = [];
  public $allServices = new BehaviorSubject<FavoriteServiceHybrid[]>([]);

  private _favorites: FavoriteService[] = null;
  public $favorites = new Subject<FavoriteService[]>();

  public get isFavoriteLoaded() {
    return this._favorites != null;
  }

  private _allServicesDetails: PortalServiceDetailsItemDto[] = [];
  private _allServicesDetailsAr: PortalServiceDetailsItemDto[] = [];
  private _allServicesDetailsEn: PortalServiceDetailsItemDto[] = [];

  public loadingAllServicesWhenLanguageSwitched = false;

  public async loadFavoriteServicesHybrid(lang: string): Promise<void> {
    if (this.loadingAllServicesWhenLanguageSwitched) {
      await this.loadFavoriteServicesAsync();

      if (lang === 'ar' && this._allServicesDetailsAr.length == 0) {
        await this.loadAllServicesDetailsAsync(lang);
      } else if (lang === 'en' && this._allServicesDetailsEn.length == 0) {
        await this.loadAllServicesDetailsAsync(lang);
      }
      else{
        this._allServicesDetails = lang === 'ar' ? this._allServicesDetailsAr : this._allServicesDetailsEn;
      }

      this.combineFavoriteServicesWithMetadata();
    }
  }

  private combineFavoriteServicesWithMetadata(): void {
    const all: FavoriteServiceHybrid[] = this._allServicesDetails.map(
      (serviceDetails) => {
        const favoriteService = this.findFavoriteService(
          serviceDetails.attr.digitalservice,
        );

        const service: FavoriteServiceHybrid = {
          expanded: false,
          metadata: null,
          favorite: favoriteService,
          serviceDetails: serviceDetails,
          isFavorite: favoriteService != null,
          isFavoriteDeleted: false,
        };

        return service;
      },
    );

    this.emitFavoriteServicesWithMetadata(all);
  }

  private findFavoriteService(digitalNumber: string): FavoriteService {
    return this._favorites.find((f) => f.procedureId == digitalNumber);
  }

  private emitFavoriteServicesWithMetadata(all: FavoriteServiceHybrid[] = []) {
    this._allServices = all;

    this.$allServices.next(this._allServices);
  }

  private async loadFavoriteServicesAsync() {
    const response = await this.favoriteServicesService.get().toPromise();

    if (response.IsSuccess) {
      const favorites = response.Data.map((a) => {
        return {
          code: a.Code,
          id: a.IdString,
          procedureId: a.ProcedureId,
          url: a.Url,
          isFavorite: true,
        };
      });

      this.emitFavoriteServices(favorites);
    }
  }

  private emitFavoriteServices(favorites: FavoriteService[]) {
    this._favorites = favorites;

    this.$favorites.next(this._favorites);
  }

  private async loadAllServicesDetailsAsync(lang: string) {
    const allServicesResponse = await this.getAllServicesDetailService
      .getContentList(lang)
      .toPromise();

    this._allServicesDetails = allServicesResponse.data.items;
    if (lang === 'ar') {
      this._allServicesDetailsAr = allServicesResponse.data.items;
    } else if (lang === 'en') {
      this._allServicesDetailsEn = allServicesResponse.data.items;
    }
  }

  public async updateFavorite(
    procedureId: string,
    isFavorite: boolean,
  ): Promise<void> {
    const response = await this.favoriteServicesService
      .toggle(procedureId, isFavorite)
      .toPromise();

    if (response.IsSuccess) {
      this.updateFavoriteLocal(procedureId, isFavorite);
    }
  }

  private updateFavoriteLocal(procedureId: string, isFavorite: boolean) {
    const service = this._allServices.find(
      (service) =>
        service.serviceDetails?.attr.digitalservice == procedureId ||
        service.favorite?.procedureId == procedureId,
    );

    if (service) {
      service.isFavorite = isFavorite;
      service.isFavoriteDeleted = isFavorite == false;

      this.emitFavoriteServicesWithMetadata(this._allServices);
    }
  }

  public async fetchServiceDetails(
    procedureId: string,
    lang: string,
  ): Promise<ServiceMetaData> {
    const response = await this.metadataService
      .getServiceMetadataCustomDetails(procedureId, lang)
      .toPromise();

    return this.serviceDetailsMapper.mapMetadata(response);
  }

  private getMetadataByLanguage(
    responseData: ResponseData[],
    language: string,
  ): ResponseData {
    return responseData.filter(
      (responseItem) => responseItem.language == language,
    )[0];
  }
}

export class FavoriteServiceHybrid {
  favorite: FavoriteService;
  serviceDetails: PortalServiceDetailsItemDto;
  metadata: ServiceMetaData;
  expanded: boolean;

  isFavorite: boolean;
  isFavoriteDeleted: boolean;
}
